import { NgFor, NgIf } from '@angular/common';
import { Component, DoCheck, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ApiService } from '../../core/api.service';
import { LocalDateTimePipe } from '../../core/date-utils';
import { Produto } from '../../core/models';

@Component({
  standalone: true,
  imports: [ReactiveFormsModule, NgFor, NgIf, LocalDateTimePipe],
  template: `
    <header class="page-header">
      <div><h2>Produtos</h2><p>Cadastro das peças contadas no acabamento</p></div>
    </header>

    <nav class="tabs" aria-label="Abas de produtos">
      <button class="tab-button" type="button" [class.active]="abaAtiva === 'consulta'" (click)="selecionarAba('consulta')">Consulta</button>
      <button class="tab-button" type="button" [class.active]="abaAtiva === 'cadastro'" (click)="selecionarAba('cadastro')">{{ form.controls.cod_produto.value ? 'Editar produto' : 'Cadastrar produto' }}</button>
    </nav>

    <div class="toast-stack" *ngIf="erro || mensagem">
      <div class="toast" [class.toast-error]="erro" [class.toast-success]="!erro && mensagem" role="alert">
        <span>{{ erro || mensagem }}</span>
        <button class="toast-close" type="button" (click)="fecharAviso()" aria-label="Fechar mensagem">X</button>
      </div>
    </div>

    <section class="panel" *ngIf="abaAtiva === 'cadastro'">
      <form class="grid-form" [formGroup]="form" (ngSubmit)="salvar()" novalidate>
        <input type="hidden" formControlName="cod_produto">
        <label>Código do produto<input formControlName="codigo_produto" autofocus></label>
        <label>Valor por unidade<input formControlName="valor_unitario" inputmode="decimal"></label>
        <label class="full">Descrição<input formControlName="descricao"></label>
        <label class="check"><input type="checkbox" formControlName="ativo"> Ativo</label>
        <div class="form-actions full">
          <button type="submit" [disabled]="salvando">{{ salvando ? 'Salvando...' : 'Salvar produto' }}</button>
          <button class="ghost" type="button" (click)="novoProduto()">Limpar</button>
        </div>
      </form>
    </section>

    <table class="data-table" *ngIf="abaAtiva === 'consulta'">
      <thead><tr><th>Código</th><th>Descrição</th><th>Valor por unidade</th><th>Criado em</th><th>Status</th><th></th></tr></thead>
      <tbody>
        <tr *ngFor="let produto of produtos">
          <td data-label="Código"><strong>{{ produto.codigo_produto }}</strong></td>
          <td data-label="Descrição">{{ produto.descricao }}</td>
          <td data-label="Valor por unidade">{{ formatMoeda(produto.valor_unitario) }}</td>
          <td data-label="Criado em">{{ produto.data_criacao | localDateTime }}</td>
          <td data-label="Status"><span class="status" [class.cancelada]="!produto.ativo">{{ produto.ativo ? 'Ativo' : 'Inativo' }}</span></td>
          <td class="actions" data-label="Ações">
            <button type="button" (click)="editar(produto)">Editar</button>
            <button type="button" (click)="inativar(produto)" [disabled]="!produto.ativo">Inativar</button>
          </td>
        </tr>
      </tbody>
    </table>
  `
})
export class ProdutosComponent implements OnInit, DoCheck, OnDestroy {
  private api = inject(ApiService);
  private fb = inject(FormBuilder);
  produtos: Produto[] = [];
  erro = '';
  mensagem = '';
  salvando = false;
  abaAtiva: 'consulta' | 'cadastro' = 'consulta';
  private avisoAtual = '';
  private avisoTimer?: ReturnType<typeof setTimeout>;

  form = this.fb.nonNullable.group({
    cod_produto: [0],
    codigo_produto: ['', [Validators.required, Validators.maxLength(60)]],
    descricao: ['', [Validators.required, Validators.maxLength(200)]],
    valor_unitario: ['0,00', Validators.required],
    ativo: [true]
  });

  ngOnInit() {
    this.carregar();
  }

  ngDoCheck() {
    const aviso = this.erro || this.mensagem;
    if (aviso === this.avisoAtual) return;

    this.avisoAtual = aviso;
    this.agendarFechamentoAviso(aviso);
  }

  ngOnDestroy() {
    if (this.avisoTimer) clearTimeout(this.avisoTimer);
  }

  carregar() {
    this.api.produtos().subscribe((produtos) => (this.produtos = produtos));
  }

  selecionarAba(aba: 'consulta' | 'cadastro') {
    this.abaAtiva = aba;
    this.erro = '';
    this.mensagem = '';
  }

  fecharAviso() {
    this.erro = '';
    this.mensagem = '';
    this.avisoAtual = '';
    if (this.avisoTimer) clearTimeout(this.avisoTimer);
  }

  salvar() {
    this.erro = '';
    this.mensagem = '';
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.erro = 'Informe código e descrição do produto';
      return;
    }

    this.salvando = true;
    const raw = this.form.getRawValue();
    const payload = {
      ...raw,
      cod_produto: raw.cod_produto || undefined,
      codigo_produto: raw.codigo_produto.trim().toUpperCase(),
      descricao: raw.descricao.trim(),
      valor_unitario: this.parseValor(raw.valor_unitario)
    };

    this.api.salvarProduto(payload).subscribe({
      next: (produto) => {
        this.novoProduto();
        this.mensagem = `Produto ${produto.codigo_produto} salvo com sucesso`;
        this.abaAtiva = 'consulta';
        this.carregar();
      },
      error: (error) => {
        this.erro = error.error?.message || 'Erro ao salvar produto';
        this.salvando = false;
      },
      complete: () => (this.salvando = false)
    });
  }

  editar(produto: Produto) {
    this.erro = '';
    this.mensagem = '';
    this.form.patchValue({
      ...produto,
      valor_unitario: this.formatNumero(produto.valor_unitario || 0)
    });
    this.abaAtiva = 'cadastro';
  }

  inativar(produto: Produto) {
    this.erro = '';
    this.mensagem = '';
    this.api.inativarProduto(produto.cod_produto).subscribe({
      next: () => {
        this.mensagem = `Produto ${produto.codigo_produto} inativado com sucesso`;
        this.carregar();
      },
      error: (error) => (this.erro = error.error?.message || 'Erro ao inativar produto')
    });
  }

  novoProduto() {
    this.erro = '';
    this.mensagem = '';
    this.form.reset({ cod_produto: 0, codigo_produto: '', descricao: '', valor_unitario: '0,00', ativo: true });
  }

  private agendarFechamentoAviso(aviso: string) {
    if (this.avisoTimer) clearTimeout(this.avisoTimer);
    if (!aviso) return;

    this.avisoTimer = setTimeout(() => this.fecharAviso(), 20000);
  }

  formatMoeda(value: string | number | null | undefined) {
    return this.parseValor(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  private parseValor(value: string | number | null | undefined) {
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    const text = String(value || '0').replace(/\s/g, '').replace(/^R\$/i, '');
    const normalized = text.includes(',') ? text.replace(/\./g, '').replace(',', '.') : text;
    const parsed = Number(normalized || 0);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private formatNumero(value: number) {
    return Number(value || 0).toFixed(2).replace('.', ',');
  }
}
